import React, { useMemo, useState, useCallback } from "react";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";
import classNames from "classnames";
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import Select from "components/Select";
import { useGetCommunicationsTemplates, useCreateCommunicationsTemplate } from "api/communications-templates/service";
import { useNavigate } from "react-router-dom";

const CommunicationTemplates = ({
  activeColor = "blue",
  account,
  shellThemeVariant = "1",
  isLightTheme = false,
}) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const isTheme2 = shellThemeVariant === "2";
  const isDarkListTheme = !isLightTheme;
  const templateNameCellColor = isDarkListTheme ? "#ffffff" : "#333";
  const filterLabelColor = isDarkListTheme ? "#ffffff" : "#666";
  const listCellTextColor = isDarkListTheme ? "#ffffff" : undefined;
  const filterSelectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const contentClassName = classNames("content", "communication-templates-page", {
    "communication-templates-page--theme2": isTheme2,
    "theme2-admin-content-compact": isTheme2,
    "theme2-communication-templates-inset": isTheme2,
  });
  const navigate = useNavigate();

  // Filter States
  const [datasource, setDatasource] = useState({ label: "All Datasources", value: "all" });
  const [templateFilter, setTemplateFilter] = useState({ label: "Active", value: "active" });

  // API Parameters
  const apiParams = useMemo(() => ({
    data_source: datasource.value === "all" ? undefined : datasource.value,
    status: templateFilter.value === "all" ? undefined : templateFilter.value,
  }), [datasource, templateFilter]);

  // Data Fetching
  const { data: apiData, isLoading } = useGetCommunicationsTemplates(apiParams);

  // Create Mutation for Copying
  const createMutation = useCreateCommunicationsTemplate();

  // Transforming API data for table
  const data = useMemo(() => {
    // Support multiple response formats: {data: []}, {results: []}, or []
    const rawList = apiData?.data || apiData?.results || (Array.isArray(apiData) ? apiData : []);

    return rawList.map(item => ({
      id: item.documentId || item.id,
      name: item.name || item.template_name || "N/A",
      description: item.description || "",
      template_id: item.template_id,
      datasource_value: item.data_source || item.datasource,
      datasource: (item.data_source || item.datasource || "N/A").toUpperCase(),
      status: item.status || (item.is_active === false ? "Inactive" : "Active"),
      raw: item, // Keep raw item for copying
    }));
  }, [apiData]);

  const handleCreateCopy = useCallback((e, rowData) => {
    e.stopPropagation(); // Prevents row click (detail navigation)

    const copyData = {
      name: `${rowData.name} - Copy`,
      description: rowData.description,
      data_source: rowData.datasource_value,
    };

    createMutation.mutate(copyData);
  }, [createMutation]);

  // ReactTable Column Definitions
  const columns = useMemo(() => [
    {
      Header: "Template Name",
      accessor: "name",
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <span className="font-weight-bold mr-3" style={{ color: templateNameCellColor }}>
            {row.original.name}
          </span>
          <Button
            color="info"
            outline
            size="sm"
            style={{
              fontSize: '10px',
              padding: '2px 10px',
              height: 'auto',
              borderWidth: '1px',
            }}
            onClick={(e) => handleCreateCopy(e, row.original)}
            disabled={createMutation.isLoading}
          >
            {createMutation.isLoading ? 'COPYING...' : 'CREATE COPY'}
          </Button>
        </div>
      )
    },
    {
      Header: "Datasource",
      accessor: "datasource",
      Cell: ({ value }) => (
        <span style={listCellTextColor ? { color: listCellTextColor } : undefined}>
          {value}
        </span>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <span
          style={{
            justifyContent: "flex-end",
            display: "flex",
            ...(listCellTextColor ? { color: listCellTextColor } : {}),
          }}
        >
          {value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()}
        </span>
      ),
    },
  ], [createMutation.isLoading, handleCreateCopy, templateNameCellColor, listCellTextColor]);

  const TemplatesTable = isTheme2 ? ReactTable2 : ReactTable;

  return (
    <div className={contentClassName}>
      <style>
          {`
          .communication-templates-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .communication-templates-card-header h2,
          .communication-templates-card-header h4 {
            font-size: 16px !important;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .communication-templates-page .communication-templates-card-header .align-items-center {
            margin-top: 3px;
          }
          .communication-templates-page .card .card-body {
            padding-top: 0px !important;
            padding-bottom: 0 !important;
            padding-left: 15px;
            padding-right: 15px;
          }
          .communication-templates-page.communication-templates-page--theme2 .communication-templates-filters-row {
            padding-top: 10px;
          }
          .communication-templates-page.communication-templates-page--theme2 .communication-templates-results-count {
            padding-top: 4px;
            padding-bottom: 6px;
          }
          body:not(.white-content) .communication-templates-page .communication-templates-filters-row label {
            color: #ffffff !important;
          }
          body:not(.white-content) .communication-templates-page .sticky-stock-table .rt-tbody .rt-td:first-child .font-weight-bold {
            color: #ffffff !important;
          }
          body:not(.white-content) .communication-templates-page .sticky-stock-table .rt-thead .rt-th:nth-child(2) .rt-resizable-header-content,
          body:not(.white-content) .communication-templates-page .sticky-stock-table .rt-thead .rt-th:nth-child(3) .rt-resizable-header-content {
            color: #ffffff !important;
          }
          body:not(.white-content) .communication-templates-page .sticky-stock-table .rt-tbody .rt-td:nth-child(2),
          body:not(.white-content) .communication-templates-page .sticky-stock-table .rt-tbody .rt-td:nth-child(3) {
            color: #ffffff !important;
          }
          .sticky-stock-table .rt-table {
            width: 100%;
            min-width: 100%;
            border-collapse: collapse;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) {
            text-align: center !important;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child {
            text-align: right !important;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) .rt-resizable-header-content {
            text-align: center;
            width: 100%;
            box-sizing: border-box;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child).-cursor-pointer .rt-resizable-header-content {
            padding-right: 2em;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child .rt-resizable-header-content {
            text-align: right;
            width: 100%;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) {
            text-align: center;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:last-child {
            text-align: right;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) > * {
            max-width: 100%;
            min-width: 0;
          }
            ${getStickyStockTableStickyColumnCss("")}
        `}
      </style>
      <Card>
        <CardHeader className="communication-templates-card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">{t('routes.communicationTemplates')}</h2>
            <Button
              color="info"
              size="sm"
              style={{ ...activeColorStyle, color: 'white', margin: 0, fontWeight: '700' }}
              onClick={() => navigate('/communication-templates/add')}
            >
              <i className="tim-icons icon-simple-add mr-2" />
              Add Template
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <Row
            className={classNames("align-items-center mb-3", {
              "communication-templates-filters-row": isTheme2,
            })}
          >
            <Col
              md="12"
              className={classNames("d-flex justify-content-end", {
                "align-items-end": isTheme2,
                "align-items-center": !isTheme2,
              })}
            >
              <div className="d-flex align-items-center" style={{ gap: "20px" }}>
                <div className="d-flex align-items-center" style={{ width: '250px' }}>
                  <label className="mb-0 mr-2" style={{ fontSize: "0.8rem", whiteSpace: "nowrap", color: filterLabelColor }}>Datasource:</label>
                  <div style={{ flex: 1 }}>
                  <Select
                    activeColor={activeColor}
                    styles={filterSelectStyles}
                    value={datasource}
                    onChange={(option) => setDatasource(option)}
                    options={[
                      { label: "All Datasources", value: "all" },
                      { label: "Account", value: "account" },
                      { label: "Inventory", value: "inventory" },
                      { label: "Deal", value: "deal" },
                      { label: "Channel", value: "channel" },
                    ]}
                  />
                  </div>
                </div>
                <div className="d-flex align-items-center" style={{ width: "200px" }}>
                  <label className="mb-0 mr-2" style={{ fontSize: "0.8rem", whiteSpace: "nowrap", color: filterLabelColor }}>Status:</label>
                  <div style={{ flex: 1 }}>
                    <Select
                      activeColor={activeColor}
                      styles={filterSelectStyles}
                      value={templateFilter}
                      onChange={(option) => setTemplateFilter(option)}
                      options={[
                        { label: "All", value: "all" },
                        { label: "Active", value: "active" },
                        { label: "Inactive", value: "inactive" },
                      ]}
                    />
                  </div>
                </div>
              </div>
          </Col>
        </Row>

          <div
            className="inventory-table-wrap"
            style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
          >
            <TemplatesTable
              data={data}
              columns={columns}
              activeColor={activeColor}
              manualPagination={false}
              pageSize={10}
              className="-striped -highlight sticky-stock-table"
              noDataText="No templates found matching your criteria."
              loading={isLoading}
              loadingText="Communication templates loading..."
              disableViewportClamp={!isTheme2}
              onRowClick={(row) => {
                if (row && row.template_id) {
                  navigate(`/communication-templates/${row.template_id}`);
                }
              }}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default CommunicationTemplates;